'use client';

import { Button } from '@/components/ui/button';
import { DOMAIN, SlovLexUrl } from '@/lib/config';
import {
  documentSlug,
  extractEffectiveDateFromIri,
} from '@/lib/utils/documentMetadata';
import { ExternalLink } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';

interface LinkOptionsMenuProps {
  href: string;
  position: { x: number; y: number; placeAbove: boolean };
  visible: boolean;
  onClose: () => void;
  onOptionSelected: (url: string) => void;
  iri?: string | undefined;
}

const buildSlovLexUrl = (href: string, iri: string) => {
  if (href.startsWith('#')) {
    if (iri.startsWith('/')) {
      return `${SlovLexUrl}${iri}${href}`;
    } else {
      return `${SlovLexUrl}/${iri}${href}`;
    }
  } else if (href.startsWith('../')) {
    const cleanedHref = href.replace(/^(\.\.\/)+/, '');
    return `${SlovLexUrl}/${cleanedHref}`;
  }
  return href;
};

const buildLexomatUrl = (href: string, locale: string, iri = '') => {
  if (href.startsWith('#')) {
    if (iri.startsWith('/')) {
      return `${DOMAIN}/${locale}/${documentSlug(locale)}${iri}${href}`;
    } else {
      return `${DOMAIN}/${locale}/${documentSlug(locale)}/${iri}${href}`;
    }
  } else if (href.startsWith('../')) {
    const cleanedHref = href.replace(/^(\.\.\/)+/, '');
    // Real slov-lex cross-references usually point at a specific provision
    // — `../../../SK/ZZ/1964/40#paragraf-711` — so the href carries a
    // fragment. The query string has to go BEFORE it: `path#frag?asOf=…`
    // makes `?asOf=…` part of the fragment, where useSearchParams() never
    // sees it and the anchor scroll/highlight stops matching the element.
    const [pathPart, fragment] = cleanedHref.split('#', 2);
    const base = `${DOMAIN}/${locale}/${documentSlug(locale)}/${pathPart}`;
    const hash = fragment ? `#${fragment}` : '';
    // Issue #327 gap 1: a cross-reference to ANOTHER law must resolve to
    // that law's version as of the SAME date as the law being viewed, not
    // always its latest version. The target href is undated (the raw
    // slov-lex cross-reference), so the date travels as a query param the
    // target page's resolver reads and forwards — see
    // useGetLegislationInformation.ts / LegislationDetails.tsx.
    const effectiveDate = extractEffectiveDateFromIri(iri);
    return effectiveDate
      ? `${base}?asOf=${effectiveDate}${hash}`
      : `${base}${hash}`;
  }
  if (!iri) return href;
  return href; // Explicit return for remaining case (has iri but no special prefix)
};

export function LinkOptionsMenu({
  href,
  position,
  visible,
  onClose,
  onOptionSelected,
  iri = '',
}: LinkOptionsMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const locale = useLocale();
  const t = useTranslations('LegislationDetails.linkOptionsMenu');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <div
      ref={menuRef}
      className='absolute z-50 bg-surface-card border border-line-default rounded-lg shadow-lg p-2 w-40'
      style={{
        left: position.x,
        top: position.placeAbove ? position.y - 80 : position.y + 10,
      }}
    >
      <div className='text-xs text-content-secondary mb-2 px-2'>
        {t('openLinkIn')}
      </div>
      <Button
        variant='ghost'
        size='sm'
        className='inline-flex items-center gap-2 w-full justify-start rounded-md hover:bg-brand-primary-color/10 transition-colors py-2 cursor-pointer'
        onClick={() => {
          const url = buildLexomatUrl(href, locale, iri);
          if (url) {
            onOptionSelected(url);
          }
        }}
      >
        <ExternalLink className='w-4 h-4 text-brand-primary-color' />
        <span className='text-brand-primary-color'>Lexomat</span>
      </Button>
      <Button
        variant='ghost'
        size='sm'
        className='inline-flex items-center gap-2 w-full justify-start rounded-md hover:bg-surface-muted transition-colors py-2 cursor-pointer'
        onClick={() => {
          const url = buildSlovLexUrl(href, iri);
          if (url) {
            onOptionSelected(url);
          }
        }}
      >
        <ExternalLink className='w-4 h-4 text-status-info' />
        <span>Slov-lex</span>
      </Button>
    </div>
  );
}
